import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { FaComments } from "react-icons/fa";
import {
  Box,
  Text,
  Avatar,
  Button,
  VStack,
  HStack,
  Input,
  Icon,
  CloseButton,
  InputGroup,
  InputRightElement,
  Flex,
} from "@chakra-ui/react";

const BASE_URL = process.env.REACT_APP_DEPLOYED_BASE_URL;
const BASE_UI_URL = "https://app.creativesmatter.com";

const SashaChatbot = ({
  creatorName,
  hasInteractedWithBot,
  onClose,
  setReviewContent,
  setShowReviewModal,
  showReviewModal, // <-- add this
}) => {
  const [hasInteracted, setHasInteracted] = useState(hasInteractedWithBot);
  const [messages, setMessages] = useState([]);
  const [stage, setStage] = useState("initial");
  const [accountStatus, setAccountStatus] = useState(null);
  const chatRef = useRef(null);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [showReviewLink, setShowReviewLink] = useState(false);
  const [reviewLinkClicked, setReviewLinkClicked] = useState(false);
  const [brandRep, setBrandRep] = useState("the brand rep"); // Placeholder, replace with actual brand rep if available
  const token = localStorage.getItem("token");

  const addMessage = (from, text) => {
    setMessages((prev) => [...prev, { from, text, type: "text" }]);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      chatRef.current?.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  const showMainOptions = async () => {
    try {
      const res = await fetch(BASE_URL + "/check_account_status/", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // Ensure `token` is available in scope
        },
      });

      const data = await res.json();

      const { is_premium, has_active_subscription, current_plan, free_trial } =
        data;

      const options = [
        "Connect my email",
        "Scan my email account",
        "Track my leads",
        "Manage my membership",
      ];

      const isAllowedReviewContract =
        is_premium ||
        free_trial ||
        (has_active_subscription &&
          ["Administrative", "Complete"].includes(current_plan));

      if (isAllowedReviewContract) {
        options.push("Review Contract");
      }

      setTimeout(() => {
        addOptionsMessage(options);
        setStage("post-first");
      }, 1000);
    } catch (error) {
      console.error("Failed to fetch account status:", error);

      // fallback options without Review Contract
      setTimeout(() => {
        addOptionsMessage([
          "Connect my email",
          "Scan my email account",
          "Track my leads",
          "Manage my membership",
        ]);
        setStage("post-first");
      }, 1000);
    }
  };

  useEffect(scrollToBottom, [messages]);

  const handleUserResponse = async (response) => {
    addMessage("You", response);

    if (stage === "initial") {
      if (response === "Yes") {
        setStage("email-status");

        try {
          const token = localStorage.getItem("token");
          const res = await axios.post(
            BASE_URL + "/check-email-status/",
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (res.status === 200) {
            addMessage(
              "Sasha",
              `Great! I’ve started the process for you by creating a new scan. Click <a class="review-link" href="/profile/import">here</a> to continue the process.`
            );
            setStage("done");
          }
        } catch (error) {
          if (error.response && error.response.status === 404) {
            addMessage(
              "Sasha",
              `Awesome! I noticed that you haven’t connected your email account. Would you like to do so?`
            );
            addOptionsMessage(["Yes", "No"]);
            setStage("ask-connect");
          } else {
            addMessage(
              "Sasha",
              "Oops! Something went wrong. Please try again later."
            );
            setStage("done");
          }
        }
      }

      if (response === "Tell Me More") {
        addMessage(
          "Sasha",
          `Of course! I can scan your email account to find past deals that you’ve completed and leads. Then I’ll add brands, brand reps, and create deals to help you keep track of everything. Should I create a scan for you?`
        );
        addOptionsMessage(["Yes", "No"]);
        setStage("more-info");
      }

      if (response === "No, thanks") {
        addMessage(
          "Sasha",
          `Not a problem. If you change your mind, go to the <a class="review-link" href="/profile/import">Profile page</a> and click the “Import Data” tab.`
        );
        setStage("done");
      }
    }

    if (stage === "ask-connect") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Sounds good. Click <a class="review-link" href="https://app.creativesmatter.com/influencer/profile?tab=connect">here</a> to connect your email account.`
        );
      } else {
        addMessage(
          "Sasha",
          `No problem. If you want to connect your email account later, go to the <a class="review-link" href="https://app.creativesmatter.com/influencer/profile?tab=connect">Profile page</a> and click the “Connect” tab.`
        );
      }
      setStage("done");
    }

    if (stage === "more-info") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Great! I’ve started the process for you by creating a new scan. Click <a class="review-link" href="/profile/import">here</a> to continue the process.`
        );
      } else {
        addMessage(
          "Sasha",
          `Not a problem. If you change your mind, go to the <a class="review-link" href="/profile/import">Profile page</a> and click the “Import Data” tab.`
        );
      }
      setStage("done");
    }

    if (stage === "post-first") {
      // Fetch status once if not already fetched
      let api_res;
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(BASE_URL + "/check_account_status/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        api_res = res;
        setAccountStatus(res.data);
      } catch (err) {
        addMessage(
          "Sasha",
          "Sorry, I couldn’t fetch your account details. Please try again later."
        );
        return;
      }

      const {
        is_premium,
        track_leads,
        has_active_subscription,
        email_connected,
        current_plan,
      } = api_res.data;

      if (response === "Connect my email") {
        if (email_connected) {
          addMessage(
            "Sasha",
            `Your email account is currently connected. You can check the status <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=connect>here</a>.`
          );
        } else if (has_active_subscription || is_premium) {
          addMessage(
            "Sasha",
            `Sure. Click <a class="review-link"href=${BASE_UI_URL}/influencer/profile?tab=connect>here</a> to connect your account and start using the full power of the CM Hub.`
          );
        } else {
          addMessage(
            "Sasha",
            `I can definitely help you with connecting your email account, but first you’ll need to upgrade your account. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>here</a> to upgrade today.`
          );
        }
        showMainOptions();
      }

      if (response === "Scan my email account") {
        if (has_active_subscription || is_premium) {
          addMessage(
            "Sasha",
            `Great! I’ve started the process for you by starting a new scan. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=import>here</a> to continue the process.`
          );
        } else {
          addMessage(
            "Sasha",
            `I can definitely scan your email account, but first you’ll need to upgrade your account. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>here</a> to upgrade today.`
          );
        }
        showMainOptions();
      }

      if (response === "Track my leads") {
        if (!email_connected) {
          addMessage(
            "Sasha",
            `I can definitely help you track your deals, but first you need to connect your email account. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=connect>here</a> to do so.`
          );
          setStage("post-first");
          showMainOptions();
          return;
        }

        if (!(has_active_subscription || is_premium)) {
          addMessage(
            "Sasha",
            `I can definitely track your leads, but first you’ll need to upgrade your account. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=import>here</a> to upgrade today.`
          );
          setStage("post-first");
          showMainOptions();
          return;
        }

        if (email_connected && track_leads) {
          addMessage(
            "Sasha",
            `I have a secret... I’ve been tracking your leads. You haven’t noticed the new deals and brands I’ve added for you? Is this ok?`
          );
          setStage("track-leads-yesno");
          return;
        }

        if (email_connected && !track_leads) {
          addMessage(
            "Sasha",
            `I can help you track your leads moving forward. Is this ok?`
          );
          setStage("not-track-leads-yesno");
          return;
        }
      }
      if (response == "Manage my membership") {
        if (current_plan === "Free") {
          addMessage(
            "Sasha",
            `Sure. You’re currently using the free version of the CM Hub. If you <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>upgrade</a>, you’ll have full access to powerful tools and me as an assistant.`
          );
          setStage("post-first");
          showMainOptions();
        }

        if (current_plan === "Complete") {
          addMessage(
            "Sasha",
            `Sure. You currently have our “Complete” membership.`
          );
          setStage("post-first");
          showMainOptions();
        }
        if (current_plan === "Marketing" || current_plan === "Administrative") {
          addMessage(
            "Sasha",
            `Sure. You currently have our “${current_plan}” membership. Would you like to upgrade to our “Complete” membership to get access to our entire suite of powerful tools?`
          );
          setStage("upgrade-yesno");
        }
      }
      // --- Review Contract flow ---
      if (response === "Review Contract") {
        addMessage(
          "Sasha",
          "Gladly. Which deal with a status of `contract pending` is this contract for?"
        );
        // Fetch deals and show as options
        try {
          const token = localStorage.getItem("token");
          const dealsRes = await axios.get(
            "https://api.creativesmatter.com/contract-deals/",
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const deals = dealsRes.data.data || [];
          if (deals.length === 0) {
            addMessage("Sasha", "You have no deals available.");
            setStage("post-first");
            showMainOptions();
            return;
          }
          // Show deal names as options
          addOptionsMessage(deals.map((d) => d.deal_name));
          setStage("review-contract-select");
        } catch (err) {
          addMessage(
            "Sasha",
            "Sorry, I couldn't fetch your deals. Please try again later."
          );
          setStage("post-first");
          showMainOptions();
        }
        return;
      }
      // --- End Review Contract flow ---
    }
    // --- Handle deal selection for Review Contract ---
    if (stage === "review-contract-select") {
      // Find the selected deal's ID
      try {
        const token = localStorage.getItem("token");
        const dealsRes = await axios.get(
          "https://api.creativesmatter.com/contract-deals/",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const deals = dealsRes.data.data || [];
        const selectedDeal = deals.find((d) => d.deal_name === response);
        if (!selectedDeal) {
          addMessage(
            "Sasha",
            "Sorry, I couldn't find that deal. Please try again."
          );
          setStage("post-first");
          showMainOptions();
          return;
        }
        setSelectedDealId(selectedDeal.deal_id);
        setBrandRep(selectedDeal.brand_rep);
      } catch (err) {
        addMessage(
          "Sasha",
          "Sorry, there was a problem finding your deal. Please try again."
        );
        setStage("post-first");
        showMainOptions();
        return;
      }
      addMessage("Sasha", `Thanks. Please share with me the contract.`);
      setStage("awaiting-contract-upload");
      return;
    }
    // --- Handle contract upload ---
    if (stage === "awaiting-contract-upload") {
      // This will be handled by file input, not by button
      return;
    }
    if (stage === "track-leads-yesno") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Thanks! I’ll keep tracking your leads and helping you manage things.`
        );
      } else if (response === "No") {
        addMessage(
          "Sasha",
          `Thanks for telling me. I’ll stop tracking your leads. Let me know if you ever want me to track your leads again.`
        );
        showMainOptions();
      }
    }

    if (stage === "not-track-leads-yesno") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Great! I’ll keep an eye on any new leads and add the info to your CM Hub database`
        );
      } else if (response === "No") {
        addMessage(
          "Sasha",
          `No worries. Don’t hesitate to let me know if you change your mind in the future.`
        );
      }
      showMainOptions();
    }
    if (stage === "upgrade-yesno") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Yay! Click here to <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>upgrade</a> your membership and I look forward to being your assistant`
        );
      } else {
        addMessage(
          "Ssasha",
          `No worries. If you change your mind, go to the <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>“Membership”</a> tab on the profile page. It’s super easy to do so.`
        );
      }
      showMainOptions();
      setStage("post-first");
    }
  };

  const addOptionsMessage = (optionsArray) => {
    setMessages((prev) => [
      ...prev,
      { from: "Sasha", type: "options", options: optionsArray },
    ]);
  };

  const didSendInitialMessage = useRef(false);

  useEffect(() => {
    if (!didSendInitialMessage.current) {
      if (!hasInteracted) {
        const initialMessage = `Hi ${creatorName}! I’m Sasha, your AI assistant. Would you like me to scan your email account to help build out your CM Hub database?`;
        addMessage("Sasha", initialMessage);
        addOptionsMessage(["Yes", "Tell Me More", "No, thanks"]);
        setStage("initial");
      } else {
        const message = `Hi ${creatorName}! How can I help you today?`;
        addMessage("Sasha", message);
        // Fetch account status before showing options
        showMainOptions();
        setStage("post-first");
      }
      didSendInitialMessage.current = true;
    }
  }, []);

  // Helper to add a message with a clickable 'here' link
  const addReviewLinkMessage = () => {
    setMessages((prev) => [
      ...prev,
      {
        from: "Sasha",
        type: "review-link",
        text: `Sharing with you some notes I made while reviewing the contract. <a href="#" class="review-link">Click here</a> to review them.`,
      },
    ]);
    setShowReviewLink(true);
  };

  // Handle user response for Yes/No after modal close
  const handleReviewEmailResponse = (response) => {
    addMessage("You", response);
    if (response === "Yes") {
      addMessage(
        "Sasha",
        `All done. I sent ${
          brandRep || "the brand rep"
        } an email with my notes.`
      );
    } else if (response === "No") {
      addMessage("Sasha", "Ok. Let me know if you change your mind.");
    }
    setStage("post-first");
    showMainOptions();
  };

  // Add effect to handle review link click
  useEffect(() => {
    if (showReviewLink) {
      const handler = (e) => {
        if (e.target.classList && e.target.classList.contains("review-link")) {
          e.preventDefault();
          setShowReviewModal(true);
          setShowReviewLink(false);
          setReviewLinkClicked(true);
        }
      };
      document.addEventListener("click", handler);
      return () => document.removeEventListener("click", handler);
    }
  }, [showReviewLink, setShowReviewModal]);

  // When modal closes after review link, ask about emailing notes
  useEffect(() => {
    if (
      !showReviewModal &&
      reviewLinkClicked &&
      stage === "awaiting-review-link-click"
    ) {
      addMessage(
        "Sasha",
        `Would you like me to send ${
          brandRep || "the brand rep"
        } an email with my notes?`
      );
      setStage("review-email-yesno");
      setReviewLinkClicked(false);
    }
  }, [showReviewModal, reviewLinkClicked, stage, brandRep]);
  
  return (
    <div className="sasha-chatbox">
      <div className="sasha-chatbox-header">
        <Box bg="#1e1917" px={3} py={2} borderTopRadius="md">
          <Flex align="center">
            <Flex align="center" justify="center" flex="1">
              <Icon as={FaComments} color="white" mr={2} />
              <Text fontWeight="bold" color="white">
                Sasha Chat
              </Text>
            </Flex>
          </Flex>
        </Box>
      </div>

      <div className="sasha-chatbox-body" ref={chatRef}>
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`sasha-chatbox-message-wrapper ${
              msg.from === "You" ? "user" : "sasha"
            }`}
          >
            {msg.type !== "options" && msg.type !== "review-link" && (
              <div
                className="user-type"
                style={{
                  fontSize: "12px",
                  color: "#999",
                  marginBottom: "4px",
                  marginRight: "11px",
                  textAlign: msg.from === "You" ? "right" : "left",
                }}
              >
                {msg.from}
              </div>
            )}

            <div className="sasha-chatbox-message">
              {msg.from === "Sasha" && msg.type !== "options" && (
                <img
                  src="/images/SashaImage.jpeg"
                  alt="Sasha"
                  className="sasha-avatar"
                />
              )}

              {msg.type === "options" ? (
                <div className="sasha-options">
                  <div
                    style={{
                      marginBottom: "6px",
                      fontSize: "13px",
                      color: "#ccc",
                    }}
                  >
                    Choose a reply
                  </div>
                  {msg.options.map((option, i) => (
                    <button
                      key={i}
                      className="sasha-option-button"
                      onClick={() => handleUserResponse(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              ) : msg.type === "review-link" ? (
                <div
                  className={`sasha-bubble sasha`}
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              ) : msg.type === "typing" ? (
                <div className="sasha-bubble sasha typing-indicator">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </div>
              ) : (
                <div
                  className={`sasha-bubble ${
                    msg.from === "You" ? "user" : "sasha"
                  }`}
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="sasha-chatbox-footer">
        {/* Prevent interaction while modal is open and awaiting review link click */}
        {!(showReviewModal && stage === "awaiting-review-link-click") && (
          <>
            {stage === "review-email-yesno" && (
              <>
                <button onClick={() => handleReviewEmailResponse("Yes")}>
                  Yes
                </button>
                <button onClick={() => handleReviewEmailResponse("No")}>
                  No
                </button>
              </>
            )}
            {stage === "track-leads-yesno" && (
              <>
                <button onClick={() => handleUserResponse("Yes")}>Yes</button>
                <button onClick={() => handleUserResponse("No")}>No</button>
              </>
            )}
            {stage === "not-track-leads-yesno" && (
              <>
                <button onClick={() => handleUserResponse("Yes")}>Yes</button>
                <button onClick={() => handleUserResponse("No")}>No</button>
              </>
            )}
            {stage === "upgrade-yesno" && (
              <>
                <button onClick={() => handleUserResponse("Yes")}>Yes</button>
                <button onClick={() => handleUserResponse("No")}>No</button>
              </>
            )}
            {stage === "awaiting-contract-upload" && (
              <ContractUploadInput
                onUpload={async (file) => {
                  addMessage("You", `Uploaded: ${file.name}`);
                  addMessage(
                    "Sasha",
                    "Great! Give me a few minutes while I review the contract and provide feedback."
                  );
                  setMessages((prev) => [
                    ...prev,
                    {
                      from: "Sasha",
                      type: "typing", // custom type
                    },
                  ]);

                  try {
                    const token = localStorage.getItem("token");
                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("deal_id", selectedDealId);
                    const gptRes = await axios.post(
                      "https://api.creativesmatter.com/review-contract/",
                      formData,
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "multipart/form-data",
                        },
                      }
                    );
                    setMessages((prev) =>
                      prev.filter((msg) => msg.type !== "typing")
                    );

                    addReviewLinkMessage();
                    setReviewContent(gptRes.data?.review?.response);
                    setStage("awaiting-review-link-click");
                  } catch (err) {
                    setMessages((prev) =>
                      prev.filter(
                        (msg) =>
                          msg.text !==
                          "Reviewing contract... (this may take a moment)"
                      )
                    );
                    addMessage(
                      "Sasha",
                      "Sorry, there was a problem uploading or reviewing your contract. Please try again."
                    );
                  }
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SashaChatbot;

// ContractUploadInput component
function ContractUploadInput({ onUpload }) {
  const fileInputRef = useRef();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        type="file"
        accept=".pdf,.doc,.docx"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) onUpload(file);
          e.target.value = ""; // reset input
        }}
      />
      <button
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        style={{ padding: "6px 12px", fontSize: 14 }}
      >
        Upload Contract
      </button>
    </div>
  );
}
